import React, { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelayClipPlayer, type RelayClipPlayerHandle } from "./RelayClipPlayer";

// Zona horaria de producción (misma que usa el resto del módulo para los TC).
const RELAY_TZ = "America/Mexico_City";

// El selector STREAM 1/2 del player mapea a los canales de video del relay.
function streamToChannel(stream: "1" | "2"): string {
  return stream === "2" ? "video-2" : "video-1";
}

// Formatea un wall-clock (UTC ms) al TC "HH:MM:SS" en hora de producción.
function formatWallTc(ms: number): string {
  return new Date(ms).toLocaleTimeString("en-GB", {
    hour12: false,
    timeZone: RELAY_TZ,
  });
}

// Convierte una fecha/hora escrita por el usuario EN HORA DE PRODUCCIÓN
// (America/Mexico_City) al instante UTC correspondiente. Replica la técnica de
// parseRelayTcToMs: interpreta los componentes como si fueran UTC, mira qué
// wall-clock muestra la TZ de producción para ese instante y corrige el offset
// (maneja el horario de verano automáticamente).
function productionWallClockToUtc(dateStr: string, timeStr: string): Date | null {
  if (!dateStr || !timeStr) return null;
  const [y, mo, d] = dateStr.split("-").map((n) => parseInt(n, 10));
  const [hh, mm, ss = 0] = timeStr.split(":").map((n) => parseInt(n, 10));
  if ([y, mo, d, hh, mm, ss].some((n) => Number.isNaN(n))) return null;

  const guess = Date.UTC(y, mo - 1, d, hh, mm, ss);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: RELAY_TZ,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: false,
  }).formatToParts(new Date(guess));
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value || "0", 10);
  let gh = get("hour");
  if (gh === 24) gh = 0;
  const shownAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    gh,
    get("minute"),
    get("second"),
  );
  const offset = guess - shownAsUtc;
  const result = new Date(guess + offset);
  return Number.isNaN(result.getTime()) ? null : result;
}

// Valores por defecto: "ahora" en hora de producción.
function nowInProductionTz(): { date: string; time: string } {
  const now = new Date();
  const date = new Intl.DateTimeFormat("en-CA", {
    timeZone: RELAY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now); // YYYY-MM-DD
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: RELAY_TZ,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  }).format(now); // HH:MM
  return { date, time };
}

const ACCENT = "var(--color-role-sp, #F72D60)";

// Panel de búsqueda de video por día/hora. El usuario elige un instante en hora
// de producción y el player carga la grabación del relay con una ventana de
// ±30 min (media hora hacia atrás y media hora hacia adelante) alrededor de él,
// para poder navegar con la barra de seek.
export const VideoSearchPanel: React.FC = () => {
  const { t } = useTranslation();
  const initial = useMemo(() => nowInProductionTz(), []);
  const [date, setDate] = useState<string>(initial.date);
  const [time, setTime] = useState<string>(initial.time);
  const [stream, setStream] = useState<"1" | "2">("1");
  const [clip, setClip] = useState<{ at: Date; channel: string } | null>(null);

  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [wallMs, setWallMs] = useState<number | null>(null);
  const [progress, setProgress] = useState<{ fraction: number; ready: boolean }>({
    fraction: 0,
    ready: false,
  });
  const [pendingSeek, setPendingSeek] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const playerRef = useRef<RelayClipPlayerHandle>(null);

  const handleSearch = useCallback(() => {
    const at = productionWallClockToUtc(date, time);
    if (!at) {
      setFormError(t("storyproducer.videoSearch.invalidDateTime", "Fecha u hora inválida."));
      return;
    }
    if (at.getTime() > Date.now()) {
      setFormError(
        t("storyproducer.videoSearch.future", "El instante seleccionado está en el futuro."),
      );
      return;
    }
    setFormError(null);
    setError(null);
    setProgress({ fraction: 0, ready: false });
    setPendingSeek(null);
    setPlaying(true);
    setClip({ at, channel: streamToChannel(stream) });
  }, [date, time, stream, t]);

  const fractionFromPointer = (clientX: number, el: HTMLElement): number | null => {
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return null;
    return Math.max(0, Math.min((clientX - rect.left) / rect.width, 1));
  };

  const seekFraction = pendingSeek ?? progress.fraction;
  const tc = wallMs != null ? formatWallTc(wallMs) : null;

  const iconBtn: React.CSSProperties = {
    background: "transparent",
    border: "none",
    color: "#ddd",
    cursor: "pointer",
    fontSize: "1.15rem",
    padding: "4px 8px",
    display: "flex",
    alignItems: "center",
  };

  return (
    <div
      data-testid="video-search-panel"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header local */}
      <div
        style={{
          padding: "12px 15px",
          background: "var(--color-bg-base)",
          borderBottom: "1px solid var(--color-panel-border)",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: ACCENT,
          fontWeight: 700,
          fontSize: "0.85rem",
          textTransform: "uppercase",
          letterSpacing: "1px",
          flexShrink: 0,
        }}
      >
        <i className="bx bx-time-five" style={{ fontSize: "1.1rem" }}></i>
        {t("storyproducer.videoSearch.title", "Buscar video")}
      </div>

      {/* Formulario día/hora */}
      <div
        style={{
          padding: "16px",
          display: "flex",
          flexWrap: "wrap",
          gap: "12px",
          alignItems: "flex-end",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          flexShrink: 0,
        }}
      >
        <label
          style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "0.75rem" }}
        >
          <span style={{ color: "#999" }}>{t("storyproducer.videoSearch.day", "Día")}</span>
          <input
            data-testid="video-search-date"
            type="date"
            value={date}
            max={nowInProductionTz().date}
            onChange={(e) => setDate(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={inputStyle}
          />
        </label>
        <label
          style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "0.75rem" }}
        >
          <span style={{ color: "#999" }}>{t("storyproducer.videoSearch.time", "Hora")}</span>
          <input
            data-testid="video-search-time"
            type="time"
            step={1}
            value={time}
            onChange={(e) => setTime(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={inputStyle}
          />
        </label>
        <button
          data-testid="video-search-submit"
          onClick={handleSearch}
          style={{
            padding: "9px 20px",
            background: ACCENT,
            border: "none",
            borderRadius: "8px",
            color: "#fff",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <i className="bx bx-search"></i>
          {t("storyproducer.videoSearch.search", "Buscar")}
        </button>
        {formError && (
          <span style={{ color: "#f72d60", fontSize: "0.78rem", alignSelf: "center" }}>
            {formError}
          </span>
        )}
      </div>

      {/* Player */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px" }}>
        <div
          style={{
            width: "100%",
            background: "var(--color-bg-base)",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.05)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Área de video 16:9 */}
          <div
            style={{
              width: "100%",
              aspectRatio: "16/9",
              background: "#000",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {clip && (
              <RelayClipPlayer
                ref={playerRef}
                at={clip.at}
                channel={clip.channel}
                autoPlayOnLoad={playing}
                onPlayingChange={setPlaying}
                onLoadingChange={setLoading}
                onWallClockChange={setWallMs}
                onProgressChange={setProgress}
                onError={setError}
              />
            )}

            {!clip && (
              <div
                style={{
                  textAlign: "center",
                  color: ACCENT,
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  padding: "0 24px",
                }}
              >
                {t(
                  "storyproducer.videoSearch.pickMoment",
                  "Elige un día y una hora, y presiona Buscar",
                )}
              </div>
            )}

            {clip && loading && !error && (
              <div
                style={{
                  position: "absolute",
                  color: "rgba(255,255,255,0.6)",
                  fontSize: "0.9rem",
                  pointerEvents: "none",
                }}
              >
                {t("common.loading", "LOADING...")}
              </div>
            )}

            {clip && error && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  color: "rgba(255,255,255,0.7)",
                  textAlign: "center",
                  padding: "24px",
                }}
              >
                <i
                  className="bx bx-error-circle"
                  style={{ fontSize: "2rem", color: "#f72d60" }}
                ></i>
                <span style={{ fontSize: "0.9rem", maxWidth: "440px" }}>{error}</span>
              </div>
            )}
          </div>

          {/* Barra de controles */}
          {clip && (
            <div
              style={{
                background: "rgba(20,20,20,0.9)",
                display: "flex",
                flexDirection: "column",
                borderTop: "1px solid rgba(255,255,255,0.05)",
              }}
            >
              {/* Seek bar (±30 min alrededor del instante buscado) */}
              <div
                onPointerDown={(e) => {
                  if (!progress.ready) return;
                  e.currentTarget.setPointerCapture(e.pointerId);
                  const f = fractionFromPointer(e.clientX, e.currentTarget);
                  if (f != null) setPendingSeek(f);
                }}
                onPointerMove={(e) => {
                  if (e.buttons !== 1) return;
                  if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;
                  const f = fractionFromPointer(e.clientX, e.currentTarget);
                  if (f != null) setPendingSeek(f);
                }}
                onPointerUp={(e) => {
                  if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                    e.currentTarget.releasePointerCapture(e.pointerId);
                  }
                  const f = fractionFromPointer(e.clientX, e.currentTarget);
                  setPendingSeek(null);
                  if (progress.ready && f != null) playerRef.current?.seekToFraction(f);
                }}
                style={{
                  position: "relative",
                  height: "14px",
                  display: "flex",
                  alignItems: "center",
                  cursor: progress.ready ? "pointer" : "default",
                  padding: "0 12px",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "4px",
                    borderRadius: "2px",
                    background: "rgba(255,255,255,0.15)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${Math.round(seekFraction * 100)}%`,
                      background: ACCENT,
                      borderRadius: "2px",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      left: `${Math.round(seekFraction * 100)}%`,
                      top: "50%",
                      width: "11px",
                      height: "11px",
                      borderRadius: "50%",
                      background: "#fff",
                      transform: "translate(-50%, -50%)",
                      boxShadow: "0 0 4px rgba(0,0,0,0.6)",
                    }}
                  />
                </div>
              </div>

              {/* Botones de transporte */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "6px 12px 10px 12px",
                }}
              >
                <button
                  style={iconBtn}
                  title={t("storyproducer.videoSearch.back30", "-30s")}
                  onClick={() => playerRef.current?.seekBy(-30)}
                >
                  <i className="bx bx-rewind"></i>
                </button>
                <button
                  style={iconBtn}
                  title={t("storyproducer.videoSearch.back5", "-5s")}
                  onClick={() => playerRef.current?.seekBy(-5)}
                >
                  <i className="bx bx-skip-previous"></i>
                </button>
                <button
                  data-testid="video-search-playpause"
                  style={{ ...iconBtn, fontSize: "1.5rem", color: ACCENT }}
                  title={t("storyproducer.videoSearch.playPause", "Play / Pausa")}
                  onClick={() => playerRef.current?.togglePlay()}
                >
                  <i className={`bx ${playing ? "bx-pause" : "bx-play"}`}></i>
                </button>
                <button
                  style={iconBtn}
                  title={t("storyproducer.videoSearch.fwd5", "+5s")}
                  onClick={() => playerRef.current?.seekBy(5)}
                >
                  <i className="bx bx-skip-next"></i>
                </button>
                <button
                  style={iconBtn}
                  title={t("storyproducer.videoSearch.fwd30", "+30s")}
                  onClick={() => playerRef.current?.seekBy(30)}
                >
                  <i className="bx bx-fast-forward"></i>
                </button>

                <span
                  data-testid="video-search-tc"
                  style={{
                    marginLeft: "8px",
                    fontFamily: "monospace",
                    fontSize: "0.82rem",
                    color: "#ddd",
                  }}
                >
                  {tc || "--:--:--"}
                </span>

                <div style={{ flex: 1 }} />

                {/* STREAM 1/2 */}
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center", fontSize: "0.8rem" }}
                >
                  {(["1", "2"] as const).map((s, i) => (
                    <React.Fragment key={s}>
                      {i > 0 && <span style={{ color: "#444" }}>|</span>}
                      <span
                        onClick={() => {
                          if (stream === s) return;
                          setStream(s);
                          setClip((prev) =>
                            prev ? { ...prev, channel: streamToChannel(s) } : prev,
                          );
                        }}
                        style={{
                          color: stream === s ? ACCENT : "#888",
                          cursor: "pointer",
                          fontWeight: stream === s ? 600 : 400,
                        }}
                      >
                        STREAM {s}
                      </span>
                    </React.Fragment>
                  ))}
                </div>

                <button
                  style={iconBtn}
                  title={t("storyproducer.videoSearch.fullscreen", "Pantalla completa")}
                  onClick={() => playerRef.current?.requestFullscreen()}
                >
                  <i className="bx bx-fullscreen"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  background: "var(--color-bg-base, #1a1a1a)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "6px",
  color: "#eee",
  padding: "8px 10px",
  fontSize: "0.85rem",
  colorScheme: "dark",
};

export default VideoSearchPanel;
